import {
  Opacity2,
  Opacity3,
  Opacity4,
  OpacityDark1,
  OpacityDark2,
  OpacityDark3,
} from '@trello/colors';
import type {
  BackgroundBrightness,
  DynamicVariables,
} from './BoardTheme.types';

/**
 * Assign dynamic background colors for buttons that are rendered directly atop
 * a board background based on backgroundBrightness.
 *
 * Historically, we fetched data about board backgrounds in a variety of
 * different components to determine backgroundBrightness prior to rendering,
 * and bifurcating Nachos button appearance between `transparent` and
 * `transparent-dark` to suit the backgroundBrightness.
 *
 * Now that we have access to CSS variables, we can rely wholly on these, and
 * remove all of this type of logic from render flows. Moving forward, these
 * values can be used directly, and we can deprecate:
 *
 * - Nachos: transparent vs transparent-dark
 * - LESS: .m-board-btn-darkbg vs .m-board-btn-lightbg mixins
 */
export const getDynamicButtonBackgroundColors = (
  backgroundBrightness: BackgroundBrightness,
  isThemeEnabled: boolean,
): Pick<
  DynamicVariables,
  | 'dynamicButtonBackgroundColor'
  | 'dynamicButtonBackgroundColorHovered'
  | 'dynamicButtonBackgroundColorPressed'
> => {
  if (!isThemeEnabled) {
    if (backgroundBrightness === 'dark') {
      return {
        // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage -- EXCEPTION (opacity)
        dynamicButtonBackgroundColor: Opacity2,
        // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage -- EXCEPTION (opacity)
        dynamicButtonBackgroundColorHovered: Opacity3,
        // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage -- EXCEPTION (opacity)
        dynamicButtonBackgroundColorPressed: Opacity4,
      };
    } else {
      return {
        // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage -- EXCEPTION (opacity)
        dynamicButtonBackgroundColor: OpacityDark1,
        // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage -- EXCEPTION (opacity)
        dynamicButtonBackgroundColorHovered: OpacityDark2,
        // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage -- EXCEPTION (opacity)
        dynamicButtonBackgroundColorPressed: OpacityDark3,
      };
    }
  }

  if (backgroundBrightness === 'dark') {
    return {
      dynamicButtonBackgroundColor: 'transparent',
      // Equivalent to --ds-background-neutral-hovered in ADS dark mode:
      dynamicButtonBackgroundColorHovered: '#A6C5E229',
      // Equivalent to --ds-background-neutral-pressed in ADS dark mode:
      dynamicButtonBackgroundColorPressed: '#BFDBF847',
    };
  } else {
    return {
      dynamicButtonBackgroundColor: 'transparent',
      // Equivalent to --ds-background-neutral-hovered in ADS light mode:
      dynamicButtonBackgroundColorHovered: '#091E4224',
      // Equivalent to --ds-background-neutral-pressed in ADS light mode:
      dynamicButtonBackgroundColorPressed: '#091E424F',
    };
  }
};
